import React, { useEffect, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';

export const LoginScreen: React.FC = () => {
  const { login, isLoading, loginMessage, clearLoginMessage } = useAuth();
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearLoginMessage();

    const result = await login(email);

    if (result.success) {
      // Update all states together to prevent race conditions
      setSubmittedEmail(email);
      setEmailSubmitted(true);
      setEmail('');
      setCooldown(30);
    } else {
      setEmailSubmitted(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#050614] to-[#0f1a2f]">
      <div className="bg-[#0c1222] border border-[#1a2d4d] rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/images/WIA Logo.png" alt="WIA Logo" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#4f46e5] via-[#06b6d4] to-[#10b981]">
            Welcome to WIA Game Lab
          </h1>
          <p className="text-gray-400 mt-2">Sign in to Interactive Flow Tool</p>
        </div>

        {!emailSubmitted ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cn(
                  'w-full px-4 py-2 bg-[#0a0f1b] border border-[#1a2d4d] rounded-lg',
                  'focus:ring-2 focus:ring-[#4f46e5] focus:border-transparent',
                  'transition-colors duration-200 text-white placeholder-gray-500'
                )}
                placeholder="you@example.com"
                required
                disabled={isLoading}
              />
            </div>

            {loginMessage?.type === 'error' && (
              <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg text-sm">
                {loginMessage.message}
              </div>
            )}

            {loginMessage?.type === 'success' && (
              <div className="bg-green-900/20 border border-green-500/50 text-green-400 px-4 py-2 rounded-lg text-sm">
                {loginMessage.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'w-full py-3 px-4 bg-[#4f46e5] text-white rounded-lg font-medium',
                'hover:bg-[#4338ca] focus:outline-none focus:ring-2 focus:ring-[#4f46e5] focus:ring-offset-2 focus:ring-offset-[#0c1222]',
                'transition-colors duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Sending magic link...
                </span>
              ) : (
                'Send Magic Link'
              )}
            </button>
          </form>
        ) : (
          <div className="space-y-6">
            {loginMessage?.type === 'success' && (
              <div className="bg-green-900/20 border border-green-500/50 text-green-400 px-4 py-2 rounded-lg text-sm">
                {loginMessage.message}
              </div>
            )}

            {loginMessage?.type === 'error' && (
              <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg text-sm">
                {loginMessage.message}
              </div>
            )}

            <p className="text-gray-300 text-sm">
              We sent a sign-in link to <span className="font-medium">{submittedEmail}</span>. It
              may take a few minutes to arrive.
            </p>

            <button
              onClick={async () => {
                clearLoginMessage();
                const result = await login(submittedEmail);
                if (result.success) {
                  setCooldown(30);
                }
              }}
              disabled={cooldown > 0 || isLoading}
              className={cn(
                'w-full py-3 px-4 rounded-lg font-medium',
                cooldown > 0 || isLoading
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-[#4f46e5] text-white hover:bg-[#4338ca] transition-colors duration-200'
              )}
            >
              {cooldown > 0 ? `Resend (${cooldown}s)` : 'Resend Email'}
            </button>

            <button
              onClick={() => {
                setEmailSubmitted(false);
                clearLoginMessage();
              }}
              className="w-full py-2 text-gray-400 hover:text-white text-sm"
            >
              Use a different email
            </button>
          </div>
        )}

        <div className="mt-6 text-center text-sm text-gray-400">
          <p>Access is by invitation only. Contact support if you need assistance.</p>
        </div>
      </div>
    </div>
  );
};
